import { formatDateWithWeekday, getISODate } from '@/lib/dates'
import { type DayGroup, detectOverlaps, sumWorkHours } from '@/lib/group-entries-by-date'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

export interface UserDayGroup {
    dateKey: string
    dayGroup: DayGroup
}

export interface UserSection {
    userId: string
    userName: string
    userColor?: string | null
    totalWorkHours: number
    pendingCount: number
    data: UserDayGroup[]
}

export function groupEntriesByUserThenDay(
    entriesByUser: Record<string, TimeEntriesExpanded[]>,
    userColorMap?: Map<string, string>
): UserSection[] {
    const sections: UserSection[] = []

    for (const [userId, entries] of Object.entries(entriesByUser)) {
        if (entries.length === 0) continue

        const dayMap = new Map<string, TimeEntriesExpanded[]>()
        for (const entry of entries) {
            const dateKey = getISODate(entry.clock_in)
            let dayEntries = dayMap.get(dateKey)
            if (!dayEntries) {
                dayEntries = []
                dayMap.set(dateKey, dayEntries)
            }
            dayEntries.push(entry)
        }

        const userDayGroups: UserDayGroup[] = []
        let totalWorkHours = 0
        let pendingCount = 0

        for (const [dateKey, dayEntries] of dayMap) {
            dayEntries.sort(
                (a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime()
            )

            const workHours = sumWorkHours(dayEntries)
            totalWorkHours += workHours
            pendingCount += dayEntries.filter(e => e.status === 'pending').length

            const overlappingEntryIds = detectOverlaps(dayEntries)

            userDayGroups.push({
                dateKey,
                dayGroup: {
                    dateKey,
                    dateLabel: formatDateWithWeekday(dateKey),
                    entries: dayEntries,
                    totalWorkHours: workHours,
                    entryIds: dayEntries.map(e => e.id),
                    hasOverlap: overlappingEntryIds.size > 0,
                    overlappingEntryIds,
                },
            })
        }

        userDayGroups.sort((a, b) => b.dateKey.localeCompare(a.dateKey))

        sections.push({
            userId,
            userName: entries[0]?.expand?.user?.name || 'Unknown User',
            userColor: userColorMap?.get(userId),
            totalWorkHours,
            pendingCount,
            data: userDayGroups,
        })
    }

    sections.sort((a, b) => a.userName.localeCompare(b.userName))

    return sections
}
